/**
 * BTS — LOVE YOURSELF 轉 ‘Tear’ · songs.
 *
 * Source of truth: `/Users/ivanmunguia/projects/nyx/tmp/bts_landing_songs.json`.
 * Analytical text is forwarded verbatim except for schema-fit flattening:
 *
 *   - `n`           : track position from the album drop, not the song-index
 *                     from the generated song JSON.
 *   - `duration`    : album-track duration formatted as M:SS.
 *   - `perspective` : flattened from `perspective.description`.
 *   - `voice`       : flattened from `perspective.voice`.
 *   - `alternative` : `alternative_readings[0]`.
 *   - `live`        : `live_tips` renamed.
 *
 * The JSON also ships `perspective.addressing` and full lyrics; the current
 * landing schema has no rendered slot for either, so they are not included.
 */

import type { SongMeaning } from '@/types/album';

export const songs: SongMeaning[] = [
  {
    n: 2,
    title: 'Fake Love',
    duration: '4:02',
    summary:
      '"Fake Love" duele porque no trata solo de un amor falso hacia otra persona. Trata de lo que pasa cuando intentás merecer amor escondiendo tus grietas, hasta que el personaje que construiste ocupa tu lugar. Context: dentro de la era "Love Yourself", la canción aprieta esa idea hasta el punto más oscuro, donde amar mal también destruye la propia identidad.',
    perspective:
      'La voz habla desde el momento en que entiende que su entrega estaba llena de actuación. Quiere amar de verdad, pero teme que para sostener ese amor tuvo que mutilarse emocionalmente.',
    voice: 'Primera persona confesional, herida y cada vez más desorientada.',
    themes: [
      'Pérdida de identidad por amor',
      'Autoengaño emocional',
      'La violencia de esconder la vulnerabilidad',
    ],
    tone: ['dolido', 'desorientado', 'catártico'],
    symbols: [
      {
        quote: '피울 수 없는 꽃을 키웠어',
        meaning:
          'La flor que no puede abrir resume un amor cultivado en condiciones imposibles. Hay cuidado, deseo y esfuerzo, pero todo nace dentro de una fantasía que no puede sostener vida real.',
      },
      {
        quote: '거울에다 지껄여봐',
        meaning:
          'El espejo concentra la crisis de identidad. Ya no alcanza con preguntarle al otro qué quiere. El narrador se enfrenta a su propia cara y tampoco encuentra respuesta.',
      },
      {
        quote: '너의 인형이 되려 해',
        meaning:
          'La muñeca expresa sumisión emocional total. El miedo no es solo perder a alguien, también perder movimiento propio y quedar reducido a objeto moldeable.',
      },
      {
        quote: '우리만의 숲',
        meaning:
          'El bosque propio sugiere un espacio íntimo que debía ser compartido y auténtico. Cuando dice que la otra persona no estaba ahí, se siente la revelación de una intimidad imaginada.',
      },
    ],
    key_lines: [
      {
        lyric: "I'm so sick of this fake love",
        explanation:
          'Es el golpe central. La multitud la canta porque condensa el hartazgo completo: seguir llamando amor a algo que ya vació al yo. No suena a despecho simple. Suena a despertar con vergüenza y cansancio.',
      },
      {
        lyric: "Now I don't know me, who are you?",
        explanation:
          'Acá la herida se vuelve identidad rota. El problema ya no es solo la pareja. Es que el narrador se deformó tanto para sostener el vínculo que termina sin reconocerse.',
      },
      {
        lyric: '널 위해 예쁜 거짓을 빚어내',
        explanation:
          'Esta línea duele porque admite la fabricación consciente de una versión amable y falsa. Hay ternura ahí, pero también agotamiento. Amar se volvió trabajo de maquillaje emocional.',
      },
      {
        lyric: '날 지워 너의 인형이 되려 해',
        explanation:
          'Es una de las confesiones más duras del tema. La imagen de volverse muñeco deja claro que el miedo más grande es dejar de tener voluntad propia con tal de ser amado.',
      },
    ],
    alternative: {
      label: 'Common fan reading: amor romántico y máscara pública',
      description:
        'Lectura muy extendida entre fans: la canción apunta también a la relación entre ídolo y personaje público. La pista está en la repetición de máscaras, auto-borrado y extrañeza frente a la propia imagen, donde el amor parece mezclarse con la presión de encajar en una versión deseable de uno mismo.',
    },
    live: {
      mindset:
        'Vulnerable y agotado, sin defensas. La canción pide cantar desde la herida de haber cambiado demasiado para que te quieran.',
      listen_for:
        'El salto entre la confesión herida de las estrofas y el estribillo como descarga colectiva. Ahí la vergüenza privada se vuelve grito compartido.',
      line_to_feel: "I'm so sick of this fake love",
    },
  },
  {
    n: 3,
    title: 'The Truth Untold',
    duration: '4:02',
    summary:
      'La canción gira alrededor de una tensión brutal: querer ser amado y sentir que mostrar el rostro real destruiría ese amor. El dolor no viene sólo de la distancia, viene de la convicción de que el propio yo es indigno de ser visto.',
    perspective:
      'La voz suena como alguien que ama de verdad, pero está atrapado en la idea de que su yo real es feo, insuficiente y condenaría ese amor. Por eso se esconde, inventa versiones soportables de sí mismo y termina viviendo dentro de una relación imposible.',
    voice: 'Primera persona íntima, confesional y muy avergonzada.',
    themes: [
      'Vergüenza y autoocultamiento',
      'Amor imposible por miedo al rechazo',
      'Deseo que sobrevive incluso después del derrumbe',
    ],
    tone: ['dolorido', 'íntimo'],
    symbols: [
      {
        quote: '외로움이 가득히',
        meaning:
          'El jardín junta belleza y soledad. Es el lugar donde florece el deseo, pero también donde el narrador se esconde. Tiene algo de refugio y de prisión al mismo tiempo.',
      },
      {
        quote: '또 가면을 쓰고',
        meaning:
          'La máscara concentra toda la idea de identidad falsa. El narrador la necesita para acercarse, pero cada vez que la usa también se aleja más de una intimidad real.',
      },
      {
        quote: '이 모래성에 난 날 매었어',
        meaning:
          'El castillo de arena muestra una vida emocional hecha sobre algo frágil. Basta una verdad, una pérdida o un gesto de rechazo para que todo se venga abajo.',
      },
      {
        quote: '핀 너를 닮은 꽃',
        meaning:
          'La flor parecida a la persona amada es una imagen de amor idealizado. El narrador no puede entregarse a sí mismo, así que cultiva una belleza sustituta que represente lo que siente.',
      },
    ],
    key_lines: [
      {
        lyric: 'You know that I can\'t',
        explanation:
          'Ésta es la herida central. El deseo está intacto, pero aparece bloqueado por vergüenza. La canción pega porque mucha gente reconoce ese impulso de querer acercarse y, al mismo tiempo, sentir que mostrarte tal cual eres arruinaría todo.',
      },
      {
        lyric: '내겐 불러줄 이름이 없어',
        explanation:
          'Es la confesión más directa del tema. No habla sólo de timidez. Habla de una identidad rota, de sentir que ni siquiera tienes palabras para presentarte con honestidad.',
      },
      {
        lyric: 'But I still want you',
        explanation:
          'Ésta es la línea que deja a la canción suspendida en el aire. Después de toda la culpa, todo sigue reducido a un deseo que no desaparece. Por eso duele tanto en directo. No resuelve nada.',
      },
      {
        lyric: '부서진 가면을 바라보면서',
        explanation:
          'Aquí aparece el golpe final. El castillo de arena ya estaba construido sobre fragilidad, y al final lo único que queda es mirar la máscara rota. Ya no hay fantasía que sostenga el vínculo.',
      },
    ],
    alternative: {
      label: 'Amor romántico y también miedo a ser visto por quienes amas',
      description:
        'Lectura de amor dirigido al público o a una persona idealizada. La distancia duele más porque el narrador cree que sólo puede acercarse desde una versión fabricada de sí mismo, y eso encaja con la insistencia en la "mask" y en no poder "Show you me".',
    },
    live: {
      mindset:
        'Vulnerable y agotado, sin defensas. La canción pide escuchar la vergüenza como una forma de amor herido, no como simple inseguridad.',
      listen_for:
        'El salto entre la contención de las estrofas y el desgarro cada vez más desnudo del final. La canción se abre de a poco, pero nunca encuentra alivio.',
      line_to_feel: 'But I still want you',
    },
  },
  {
    n: 1,
    title: 'Singularity',
    duration: '3:17',
    summary:
      '"Singularity" duele porque pone el amor junto a la pérdida de identidad y no resuelve esa pelea. El hablante quiere ser querido, pero teme que para lograrlo haya enterrado su voz real hasta quedarse irreconocible para sí mismo.',
    perspective:
      'La voz canta desde dentro de una relación donde amar implicó borrar partes de sí mismo. Habla con culpa, miedo y una lucidez tardía que ya no puede seguir tapando la grieta.',
    voice: 'Primera persona íntima, herida y sofocada.',
    themes: [
      'Amor que exige borrar la identidad',
      'Verdad reprimida que termina emergiendo',
      'Miedo a descubrir que el yo ofrecido era una máscara',
    ],
    tone: ['sofocado', 'dolorido'],
    symbols: [
      {
        quote: '이 얼어 붙은 호수에',
        meaning:
          'El lago congelado guarda todo lo que fue reprimido. Bajo esa superficie quieta quedaron la voz, el deseo y el dolor. Cuando llegue la primavera, todo eso va a salir aunque él quiera frenarlo.',
      },
      {
        quote: '내 목소릴 널 위해 묻었 잖아',
        meaning:
          'La voz enterrada concentra el sacrificio más duro de la canción: callarse para sostener el vínculo. Ya no se trata solo de mentirle a otro, también de mutilarse por amor.',
      },
      {
        quote: '결국 엔 언젠가 봄이와',
        meaning:
          'El invierno y el hielo nombran un estado emocional de congelamiento. Sobrevive, pero sin circulación real de afecto ni de verdad. La primavera trae alivio y amenaza al mismo tiempo porque derrite lo oculto.',
      },
      {
        quote: '또 울리고있어 그 소리가',
        meaning:
          'El sonido que sigue sonando es la conciencia que vuelve una y otra vez. Puede sentirse como culpa, trauma o la verdad insistiendo desde abajo. Nunca se apaga del todo.',
      },
    ],
    key_lines: [
      {
        lyric: '내 목소릴 널 위해 묻었 잖아',
        explanation:
          'Esta línea clava la herida principal. El hablante admite que escondió su voz para conservar a alguien. Ahí aparece el precio real del vínculo: seguir amado, pero cada vez menos entero.',
      },
      {
        lyric: '나는 날 잃은 걸까',
        explanation:
          'Es el centro emocional de la canción. No sabe si el amor lo completó o lo borró. Esa duda pesa más que cualquier confesión directa porque deja ver que ya ni siquiera reconoce qué ganó y qué perdió.',
      },
      {
        lyric: '부탁해 아무 말도하지 마',
        explanation:
          'Este ruego tiene miedo puro adentro. Quiere detener la verdad antes de que salga a la superficie. La canción entera tiembla en ese intento de callar lo que ya está rompiendo el hielo.',
      },
      {
        lyric: 'Tell me 고통조차 가짜라면',
        explanation:
          'Esta es la línea que más golpea a la gente porque convierte el dolor en una pregunta moral. Si hasta el sufrimiento era falso, entonces toda la entrega quedó sin sentido. Duele por lo íntima y por lo cruel.',
      },
    ],
    alternative: {
      label: 'Yo partido por amor',
      description:
        'Lectura de identidad escindida: la canción habla de una voz interior sacrificada para sostener una versión aceptable de uno mismo en una relación. La pista está en "내 목소릴 널 위해 묻었 잖아" y en la duda "나는 날 잃은 걸까".',
    },
    live: {
      mindset:
        'Una vulnerabilidad que no pide permiso. Escúchala como alguien que ya sostuvo demasiado tiempo una máscara y siente el momento exacto en que empieza a romperse.',
      listen_for:
        'El paso del ahogo contenido al deshielo. Cuando aparece la primavera, la canción deja de esconder el daño y empieza a dejarlo correr.',
      line_to_feel: '나는 날 잃은 걸까',
    },
  },
];
